/**
 * ModelFinderSelection — the DOM-free selection model, collapsed to a SINGLE id.
 *
 * The client tracks exactly one value: the deepest selected node id
 * (`selectedId: number | null`). Every column, ancestor highlight and the
 * deep-link URL derive from it via the tree's `ancestors` spine (spec §3.5), so
 * there is no parallel name path to drift (ADR-0004 / D16).
 *
 * This replaces the old positional `{value,name}` 6-tuple and its
 * `fromUrl`/`toUrlValue`/`fromPreselect`/`fromState`/`toPathInput`/`selectedName`
 * name logic — all of which encoded the name path this refactor removes.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.5/§3.8
 */

import type { NodeId } from './brand';

export class ModelFinderSelection {
  private value: NodeId | null = null;

  constructor(initial: NodeId | null = null) {
    this.value = initial;
  }

  /** The deepest selected id, or `null` when nothing is selected. */
  get selectedId(): NodeId | null {
    return this.value;
  }

  /** Set the deepest selected id (or clear with `null`). */
  set(id: NodeId | null): void {
    this.value = id;
  }

  /** Whether a selection exists. */
  hasSelection(): boolean {
    return this.value !== null;
  }
}
